import { useState, useRef, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableContainer,
  InputAdornment,
  Modal,    
  Alert,
  AlertTitle
} from '@mui/material';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import AddIcon from '@mui/icons-material/Add';
import { SortableItem } from './SortableItem';
import { useAuth } from '../../context/AuthContext';
import { WithPermission } from '../../Auth/WithPermission';
import axios from '../../api/axios';
import ContentLayout from '../ContentLayout/ContentLayout';
import ManageDevices from './ManageDevices';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: '80%', md: 550 },
  maxWidth: 550,
  bgcolor: 'background.paper',
  borderRadius: 1,
  boxShadow: 24,
  p: 2,
  maxHeight: '90vh',
  overflow: 'auto',
};

const GET_ORG = "/org/"
const GET_PLAN = "/plan/"
const GET_BUILD_STAGE = "/buildstage/"

export const Settings = () => {
  const auth = useAuth();

  const [buildStageList, setBuildStageList] = useState([]);

  const [placementChange, setPlacementChange] = useState(false);

  useEffect(() => {
    axios.post(GET_ORG, {
      token: localStorage.getItem('token'),
      orgSettings: true
    })
      .then((response) => {
        var placementChange = response.data?.message?.settings?.placement_change_text ? true : false;
        setPlacementChange(placementChange);
        setInputValue(response.data?.message?.settings?.standard_anchor_placement);
      })
      .catch((error) => {
        console.error('Error fetching plan number:', error);
      });

    axios.post(GET_BUILD_STAGE, {
      token: localStorage.getItem('token'),
      getBuildStage: 'true',
    })
      .then((response) => {
        var tempbuildStageList = response.data?.message?.buildStages
        if (tempbuildStageList) {
          // Sort by sortOrder
          const sortedList = tempbuildStageList.sort((a, b) => a.sortOrder - b.sortOrder);
          setBuildStageList(sortedList);
        }
      })
      .catch((error) => {
        console.error('Error fetching build stages:', error);
      });
  }, [])

  /////////////////////////////////////////////////////////////////////////////////////////
  // STANDARD ANCHOR PLACEMENT TEXT INPUT
  /////////////////////////////////////////////////////////////////////////////////////////

  const [openUpdateNotes, setOpenUpdateNotes] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [previousValue, setPreviousValue] = useState('');
  const [showUpdateButton, setShowUpdateButton] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [openUnsavedChanges, setOpenUnsavedChanges] = useState(false);
  const textFieldRef = useRef(null);
  const updateButtonRef = useRef(null);

  const handleCloseUnsavedChanges = () => setOpenUnsavedChanges(false);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    if (value !== previousValue) {
      setIsDirty(true);
      setShowUpdateButton(true);
    } else {
      setIsDirty(false);
      setShowUpdateButton(false);
    }
  };

  const [lastKeyPressed, setLastKeyPressed] = useState(null);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && showUpdateButton) {
      e.preventDefault();
      setLastKeyPressed('Enter');
      updateButtonRef.current?.click();
    }
  };

  const handleBlur = (e) => {
    const isUpdateNotesClick = updateButtonRef.current?.contains(e.relatedTarget);
    const wasEnterPressed = lastKeyPressed === 'Enter';

    if (!isUpdateNotesClick && !wasEnterPressed && isDirty && inputValue !== previousValue) {
      e.stopPropagation();
      setOpenUnsavedChanges(true);
    }

    setLastKeyPressed(null);
  };

  const handleDiscardChanges = () => {
    setInputValue(previousValue);
    setIsDirty(false);
    setOpenUnsavedChanges(false);
    setShowUpdateButton(false);
  };

  const handleContinueEditing = () => {
    setOpenUnsavedChanges(false);
    setTimeout(() => { textFieldRef.current.focus(); }, 50);
  };

  const handleFocus = () => {
    if (!isDirty) {
      setPreviousValue(inputValue);
    }
  };
  /////////////////////////////////////////////////////////////////////////////////////////
  // STANDARD ANCHOR PLACEMENT MODAL
  /////////////////////////////////////////////////////////////////////////////////////////
  const [numberOfPlansNonstandardAnchors, setNumberOfPlansNonstandardAnchors] = useState(0)
  const [wordingChangeLoading, setWordingChangeLoading] = useState(false);
  const [placementChangeLoading, setPlacementChangeLoading] = useState(false);

  const handleOpenUpdateNotes = () => {
    if (previousValue !== "") {
      axios.post(GET_PLAN, {
        token: localStorage.getItem('token'),
        returnPlansNonstandardAnchorFalse: 'true',
      })
        .then((response) => {
          console.log(response)
          let number = 0
          if (response.data?.code !== 316) {
            number = response.data?.message?.numberOfPlans;
          }
          if (number > 0) {
            setNumberOfPlansNonstandardAnchors(number)
            setOpenUpdateNotes(true)
          } else {
            handleWordingChange();
          }
        })
        .catch((error) => {
          console.error('Error fetching plan number:', error);
        });
    } else {
      handleWordingChange();
    }
  };

  const handleCloseUpdateNotesDiscard = () => {
    setOpenUpdateNotes(false);
    handleDiscardChanges();
  }

  const handleCloseUpdateNotes = () => {
    setOpenUpdateNotes(false);
  }

  const handleWordingChange = () => {
    setWordingChangeLoading(true)
    axios.post(GET_ORG, {
      token: localStorage.getItem('token'),
      wordingChange: true,
      newSAP: inputValue
    })
      .then((response) => {
        console.log(response)
        if (response.data?.code === 0) {
          setIsDirty(false);
          handleCloseUpdateNotes();
          setShowUpdateButton(false);
          console.log("Updated the org. standard anchor placement.")
        } else if (response.data?.code === 316) {
          console.log(response.data.message)
        }

      })
      .catch((error) => {
        console.error('Error updating org. standard anchor placement:', error);
      }).finally(() => {
        setWordingChangeLoading(false)
      })
  };

  const handlePlacementChange = () => {
    setPlacementChangeLoading(true)
    axios.post(GET_PLAN, {
      token: localStorage.getItem('token'),
      placementChange: true,
      newAnchorNote: inputValue
    })
      .then((response) => {

        if (response.data?.code === 0) {
          setIsDirty(false);
          handleCloseUpdateNotes();
          setShowUpdateButton(false);
          console.log("Updated " + response.data?.message?.numberOfUpdatedAnchorNotes + " plan's anchor_Note.")
        } else if (response.data?.code === 316) {
          console.log(response.data.body.message)
        }

      })
      .catch((error) => {
        console.error('Error fetching plan number:', error);
      }).finally(() => {
        setPlacementChangeLoading(false)
      })
  };

  /////////////////////////////////////////////////////////////////////////////////////////
  // BUILD STAGE TABLE
  /////////////////////////////////////////////////////////////////////////////////////////

  const [openRemoveBuildStage, setOpenRemoveBuildStage] = useState(false);
  const [newBuildStage, setNewBuildStage] = useState(false);
  const [buildStageIndex, setBuildStageIndex] = useState(-1);
  const [buildStagesNumberInPlans, setBuildStagesNumberInPlans] = useState(0);
  var tempStageIndex;

  const handleOpenRemoveBuildStage = (index) => {
    setBuildStageIndex(index)
    tempStageIndex = index
    const stageName = buildStageList[index].buildStageName;

    axios.post(GET_BUILD_STAGE, {
      token: localStorage.getItem('token'),
      getNumberOfBuildStagesInAllPlans: true,
      buildStageName: stageName
    })
      .then((response) => {

        if (response.data?.code === 0) {
          let buildStagesNumber = response.data?.message?.numberOfBuildStages;
          if (buildStagesNumber === 0) {
            // delete the build stage if is not used in any plans
            deleteBuildStage(tempStageIndex)
          } else {
            setBuildStagesNumberInPlans(buildStagesNumber)
            setOpenRemoveBuildStage(true)
          }
        } else {
          console.log(response)
        }

      })
      .catch((error) => {
        console.error('Error fetching plan number:', error);
      })
  };

  function deleteBuildStage(index) {
    const stageName = buildStageList[index].buildStageName;

    axios.post(GET_BUILD_STAGE, {
      token: localStorage.getItem('token'),
      deleteBuildStageInPlans: true,
      buildStageName: stageName
    })
      .then((response) => {
        if (response.data?.code === 0) {
          const updatedList = buildStageList.filter((_, i) => i !== index);
          setBuildStageList(updatedList);
        } else if (response.data?.code === 325) {
          console.log(response.data?.message)
        } else {
          console.log(response)
        }

      })
      .catch((error) => {
        console.error('Error deleting build stage:', error);
      })
  }

  const handleCloseRemoveBuildStage = () => setOpenRemoveBuildStage(false);

  const sensors = useSensors(
    useSensor(PointerSensor)
  );

  const handleAddStage = () => {
    // Add new empty stage at the beginning
    const newStage = { id: null, buildStageName: '', sortOrder: 0 };
    setBuildStageList([newStage, ...buildStageList]);
    setNewBuildStage(true)
  };

  const handleDeleteStageImmediate = (index) => {
    const updatedList = buildStageList.filter((_, i) => i !== index);
    setBuildStageList(updatedList);
    setNewBuildStage(false)
  };

  const handleDeleteStage = () => {
    const stageName = buildStageList[buildStageIndex].buildStageName;

    axios.post(GET_BUILD_STAGE, {
      token: localStorage.getItem('token'),
      deleteBuildStageInPlans: true,
      buildStageName: stageName
    })
      .then((response) => {
        if (response.data?.code === 0) {
          const updatedList = buildStageList.filter((_, i) => i !== buildStageIndex);
          setBuildStageList(updatedList);
        } else if (response.data?.code === 326) {
          console.log(response.data?.message)
        } else {
          console.log(response)
        }

      })
      .catch((error) => {
        console.error('Error deleting build stage:', error);
      }).finally(() => {
        setOpenRemoveBuildStage(false)
      })

  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setBuildStageList((items) => {
        const oldIndex = items.findIndex(item => item.buildStageName === active.id);
        const newIndex = items.findIndex(item => item.buildStageName === over.id);

        const newItems = arrayMove(items, oldIndex, newIndex);

        const buildStageNames = newItems.map(item => item.buildStageName);

        axios.post(GET_BUILD_STAGE, {
          token: localStorage.getItem('token'),
          reorderBuildStages: true,
          buildStageNames: buildStageNames
        })
          .then((response) => {
            if (response.data?.code === 0) {
              console.log(response.data?.message)
            } else {
              console.log(response)
            }

          })
          .catch((error) => {
            console.error('Error reordering build stages:', error);
          })

        return newItems;
      });
    }
  };

  function addNewBuildStage(newName) {
    axios.post(GET_BUILD_STAGE, {
      token: localStorage.getItem('token'),
      createBuildStage: true,
      buildStage: newName
    })
      .then((response) => {
        if (response.data?.code === 0) {
          console.log(response.data?.message)
          setBuildStageList(prevList => {
            const updatedList = [...prevList];
            updatedList[0] = response.data?.message;
            return updatedList;
          });
          console.log("saved")
        } else {
          console.log(response)
        }

      })
      .catch((error) => {
        console.error('Error saving build stage:', error);
      })
  }

  const handleStageNameChange = (index, newName) => {
    // check for duplicates
    if (buildStageList.some((stage, i) => i !== index && stage.buildStageName === newName)) {
      if (newBuildStage) {
        handleDeleteStageImmediate(index);
      }
      return false; // indicate duplicate found
    }

    const currentStage = buildStageList[index];
    const isNew = !currentStage.id;

    // Update the build stage list
    setBuildStageList(prevList => {
      const updatedList = [...prevList];
      updatedList[index] = { ...updatedList[index], buildStageName: newName };
      return updatedList;
    });

    if (isNew) {
      addNewBuildStage(newName)
    } else {
      changeBuildStageName(currentStage.id, newName)
    }

    return true; // indicate success
  };

  function changeBuildStageName(id, newName) {
    axios.post(GET_BUILD_STAGE, {
      token: localStorage.getItem('token'),
      renameBuildStage: true,
      id: id,
      newBuildStageName: newName
    })
      .then((response) => {
        if (response.data?.code === 0) {
          console.log("renamed")
        } else {
          console.log(response)
        }

      })
      .catch((error) => {
        console.error('Error renaming build stage:', error);
      })
  }

  const stageNames = buildStageList.map(stage => stage.buildStageName);

  return (
    <>
      {auth.userRole === 'useredit' &&
      <Box sx={{ paddingLeft: 5, paddingRight: 5, paddingTop: 2 }}>
        <Alert severity="info">
          <AlertTitle>Limited access for Editor role</AlertTitle>
          Only Admins can modify settings and see registration codes.
        </Alert>
      </Box>
      }
      
      <ContentLayout title={"Settings"} url={"/plans"}>

        <ManageDevices />

        <Box sx={{ paddingLeft: 3, paddingRight: 3, margin: '0 auto', backgroundColor: '#ECEFF1' }}>
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Standard Anchor Placement
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Indicate where anchors are typically placed in plans.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 3 }}>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CenterFocusStrongIcon sx={{ color: placementChange ? 'success.main' : 'action.active', mr: 1, my: 0.5 }} />
              <TextField
                id="input-with-sx-2"
                inputRef={textFieldRef}
                label="Standard Anchor Placement Notes"
                variant="standard"
                sx={{ width: 1 }}
                value={inputValue}
                onChange={handleInputChange}
                onBlur={handleBlur}
                onFocus={handleFocus}
                onKeyDown={handleKeyDown}
                helperText="For example, 'Anchors are placed at 10 feet within the CAD file.'"
                disabled={auth.userRole === 'useredit'}
                slotProps={{
                  input: {
                    endAdornment: showUpdateButton && (
                      <InputAdornment position="end">
                        <Button
                          id="open-update-notes"
                          variant="contained"
                          sx={{ mt: 2, mb: 4 }}
                          onClick={handleOpenUpdateNotes}
                          ref={updateButtonRef}
                        >
                          UPDATE NOTES
                        </Button>
                      </InputAdornment>
                    )
                  },
                  htmlInput: { maxLength: 500 }
                }}
              />
            </Box>

          </Box>

          {/* Manage Build Stages Section */}
          <Box>
            <Typography sx={{ mt: 6 }} variant="h6" gutterBottom>
              Manage Build Stages
            </Typography>
            <Typography variant="body1" gutterBottom sx={{ mb: 3 }}>
              Use build stages to help you organize how you use plans. You can order their appearance in a way that best matches your building process.
            </Typography>

            <WithPermission rolesRequired={['admin', 'superadmin']}>
              <Button
                variant="text"
                startIcon={<AddIcon />}
                onClick={handleAddStage}
                sx={{ mb: 2 }}
              >
                ADD STAGE
              </Button>
            </WithPermission>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToVerticalAxis]}
            >
              <TableContainer>
                <Table sx={{ border: 0 }}>
                  <TableBody>
                    <SortableContext
                      items={stageNames}
                      strategy={verticalListSortingStrategy}
                    >
                      {buildStageList.map((stage, index) => (
                        <SortableItem
                          key={stage.buildStageName || `new-${index}`}
                          id={stage.buildStageName}
                          stage={stage.buildStageName}
                          index={index}
                          onImmediateDelete={handleDeleteStageImmediate}
                          handleDeleteStage={() => handleOpenRemoveBuildStage(index)}
                          onStageNameChange={handleStageNameChange}
                          isNew={newBuildStage}
                          setNewBuildStage={setNewBuildStage}
                          setOpenRemoveBuildStage={setOpenRemoveBuildStage}
                        />
                      ))}
                    </SortableContext>
                  </TableBody>
                </Table>
              </TableContainer>
            </DndContext>

          </Box>

          <Modal
            open={openRemoveBuildStage}
            onClose={handleCloseRemoveBuildStage}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
          >
            <Box sx={style}>
              <Typography id="modal-modal-title" variant="h5" component="h2" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                Build Stage in use
              </Typography>
              <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                <strong>{buildStageList[buildStageIndex]?.buildStageName}</strong> is used in <strong>{buildStagesNumberInPlans}</strong> plans. Do you want to delete it and remove it from all plans?
              </Typography>
              <Box
                display="flex"
                flexDirection={{ xs: 'column', sm: 'row' }}
                gap={1}
                mt={4}
              >
                <Button
                  variant="contained"
                  color="error"
                  fullWidth={{ xs: true, sm: false }}
                  onClick={() => handleDeleteStage()}
                >
                  YES, DELETE
                </Button>
                <Box flexGrow={{ xs: 0, sm: 1 }} />
                <Button
                  variant="text"
                  fullWidth={{ xs: true, sm: false }}
                  sx={{ mt: { xs: 1, sm: 0 } }}
                  onClick={handleCloseRemoveBuildStage}
                >
                  DO NOT DELETE
                </Button>
              </Box>
            </Box>
          </Modal>

          <Modal
            open={openUpdateNotes}
            onClose={(event, reason) => {
              if (reason === "backdropClick" || reason === "escapeKeyDown") {
                return;
              }
              handleCloseUpdateNotesDiscard();
            }}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
            disableEscapeKeyDown
          >
            <Box sx={style}>
              <Typography id="modal-modal-title" variant="h5" component="h2" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                Changing Standard Anchor Placement Notes?
              </Typography>
              <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                If this is a <strong>placement change</strong> for anchors in CAD files and jobsites, we will <strong>update {numberOfPlansNonstandardAnchors} existing plans</strong> to become Non-Standard Anchor Placement Notes. We'll keep your old note on them.
                <br /><br />
                If this is a <strong>wording change</strong> or clarification, we'll show your updated note on <strong>all {numberOfPlansNonstandardAnchors} plans and any new ones</strong>.
              </Typography>
              <Box
                display="flex"
                flexDirection={{ xs: 'column', sm: 'row' }}
                mt={4}
                gap={1}
              >
                <Button variant="contained" fullWidth={{ xs: true, sm: false }}
                  loading={placementChangeLoading} loadingPosition="start"
                  onClick={handlePlacementChange}
                >
                  Placement Change
                </Button>
                <Button variant="contained" fullWidth={{ xs: true, sm: false }}
                  loading={wordingChangeLoading} loadingPosition="start"
                  onClick={handleWordingChange}
                >
                  Wording Change
                </Button>
                <Box flexGrow={{ xs: 0, sm: 1 }} />
                <Button
                  variant="text"
                  fullWidth={{ xs: true, sm: false }}
                  sx={{ mt: { xs: 1, sm: 0 }, color: "black" }}
                  onClick={handleCloseUpdateNotesDiscard}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          </Modal>

          <Modal
            open={openUnsavedChanges}
            onClose={(event, reason) => {
              if (reason === "backdropClick" || reason === "escapeKeyDown") {
                return;
              }
              handleCloseUnsavedChanges();
            }}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
            disableEscapeKeyDown
          >
            <Box sx={style}>
              <Typography id="modal-modal-title" variant="h5" component="h2" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                You have unsaved note changes.
              </Typography>
              <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                Do you want to continue editing or discard your changes?
              </Typography>
              <Box
                display="flex"
                flexDirection={{ xs: 'column', sm: 'row' }}
                gap={1}
                mt={4}
              >
                <Button
                  variant="contained"
                  fullWidth={{ xs: true, sm: false }}
                  onClick={handleContinueEditing}
                >
                  EDIT
                </Button>
                <Box flexGrow={{ xs: 0, sm: 1 }} />
                <Button
                  variant="text"
                  fullWidth={{ xs: true, sm: false }}
                  sx={{ mt: { xs: 1, sm: 0 }, color: "black" }}
                  onClick={handleDiscardChanges}
                >
                  DISCARD
                </Button>
              </Box>
            </Box>
          </Modal>
        </Box>
      </ContentLayout>
    </>
  );
};